import { useEffect, useMemo } from "react";
import { Button, ButtonGroup, HStack, Icon } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../stores/RootStore";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "../icons";

const btnProps = {
  size: "sm",
  fontSize: "sm",
  variant: "solid",
  colorPalette: "primary",
} as any;

const NodeNavControlsBase = () => {
  const { mapStore, osaNodeStore } = useStores();
  const node = osaNodeStore.getSelected()!;
  const nodes = osaNodeStore.nodesByWayId.get(node.osaWayId)!;

  const navState = useMemo(() => {
    if (nodes) {
      const selectNode = (nodeId: string) => {
        osaNodeStore.select(nodeId);
        mapStore.recenterMap();
      };
      const selectedIndex = nodes.findIndex((s) => s.id === node.id);
      if (selectedIndex === -1) return;

      const isFirst = selectedIndex === 0;
      const isLast = selectedIndex === nodes.length - 1;
      const goToPrevious = () => selectNode(nodes[selectedIndex - 1].id!);
      const goToNext = () => selectNode(nodes[selectedIndex + 1].id!);
      const goToFirst = () => selectNode(nodes[0].id!);
      const goToLast = () => selectNode(nodes[nodes.length - 1].id!);

      return { isFirst, isLast, goToPrevious, goToNext, goToFirst, goToLast };
    }

    // eslint-disable-next-line
  }, [node, nodes]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowLeft": {
          event.preventDefault();
          event.ctrlKey || event.metaKey
            ? !navState?.isFirst && navState?.goToFirst()
            : !navState?.isFirst && navState?.goToPrevious();
          break;
        }
        case "ArrowRight": {
          event.preventDefault();
          event.ctrlKey || event.metaKey
            ? !navState?.isLast && navState?.goToLast()
            : !navState?.isLast && navState?.goToNext();
          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [navState]);

  return (
    <HStack>
      <Button
        {...btnProps}
        disabled={!node || navState?.isFirst}
        onClick={navState?.goToFirst}
      >
        <Icon as={ArrowLeftIcon} m={-2} size={"lg"} />
      </Button>
      <ButtonGroup attached {...btnProps}>
        <Button
          disabled={!node || navState?.isFirst}
          onClick={navState?.goToPrevious}
        >
          <Icon as={ChevronLeftIcon} m={-2} size={"md"} />
        </Button>

        <Button
          disabled={!node || navState?.isLast}
          onClick={navState?.goToNext}
        >
          <Icon as={ChevronRightIcon} m={-2} size={"md"} />
        </Button>
      </ButtonGroup>
      <Button
        {...btnProps}
        disabled={!node || navState?.isLast}
        onClick={navState?.goToLast}
      >
        <Icon as={ArrowRightIcon} m={-2} size={"lg"} />
      </Button>
    </HStack>
  );
};

export const NodeNavControls = observer(NodeNavControlsBase);
